import { create } from 'zustand'
// Type-only import: erased at compile time, so hls.js stays out of bundles
// that never load a video (the runtime module is dynamic-imported lazily).
import type Hls from 'hls.js'
import { videoIdInScope } from '../components/card/videoIdScopeContext'

export type VideoContext =
  | 'feed'
  | 'carousel'
  | 'grid'
  | 'hero'
  | 'circle_stories'
  | 'floating_player'
  | 'player_deck'
  | 'horizontal_player'
  | 'horizontal_carousel'
  | 'dynamic_carousel'
  | 'vertical_stories'

// Contexts whose videos are always autoplay candidates. Feed, grid, hero and
// horizontal_player put a single or few cards in the viewport, and the most
// visible one plays. The remaining contexts nominate one video at a time: a
// self-managed surface presents its current card's video via
// `presentVideo(scope, …)` (carousel, player_deck, horizontal_carousel,
// dynamic_carousel, vertical_stories), or, when it is always on screen (the
// fixed floating_player, the circle_stories overlay viewer), owns the slot via
// the pinnedActiveVideoId / viewerActiveStoryId primitives.
export const MOST_VISIBLE_ELIGIBLE_CONTEXTS: VideoContext[] = [
  'feed',
  'grid',
  'hero',
  'horizontal_player',
]

/**
 * A candidate starts autoplaying once this much of it shows (or, for a video
 * taller than the viewport, once it fills this much of the viewport; see
 * `useVideoVisibility`). Once playing it keeps playing until it has fully left
 * the viewport, so a video never stops and restarts at the threshold.
 */
export const AUTOPLAY_MIN_VISIBILITY = 0.5

/**
 * How much more visible another candidate must be before an autoplaying video
 * hands playback to it. Without the margin, two partly visible videos would
 * trade playback back and forth while the viewer scrolls between them.
 */
export const AUTOPLAY_HANDOFF_MARGIN = 0.25

/**
 * Who started the active video. A viewer's press keeps its video playing
 * until it leaves the viewport or they press another; autoplay hands over to
 * a more visible candidate.
 */
export type PlaybackStarter = 'viewer' | 'autoplay'

export interface VideoInfo {
  videoId: string
  videoElement: HTMLVideoElement | null
  containerElement: HTMLElement | null
  hlsInstance: Hls | null
  visibility: number
  context: VideoContext
  isManuallyPaused: boolean
}

interface VideoPlaybackState {
  videos: Record<string, VideoInfo>
  /** Changes only when the container-to-video mapping can change. */
  containerRevision: number
  activeVideoId: string | null
  activeStartedBy: PlaybackStarter | null
  /** The most visible autoplay candidate at or above AUTOPLAY_MIN_VISIBILITY. */
  mostVisibleVideoId: string | null
  globalMuted: boolean
  userMutePreference: boolean
  // viewerActiveStoryId is set while the circle stories overlay viewer is
  // open. The story owns the playback slot: setting it pauses every other
  // video and autoplays the story; only a viewer press plays something else.
  // Only for an overlay: the owner is exempt from the visibility rules.
  viewerActiveStoryId: string | null
  // pinnedActiveVideoId is the floating player's headline. Like a story
  // viewer it owns the slot and autoplays, but a viewer press elsewhere always
  // wins; the headline resumes once the pressed video leaves the viewport.
  pinnedActiveVideoId: string | null
  /**
   * The video each self-managed surface instance presents (its scope → the
   * presented card's first video id): that video is its only autoplay
   * candidate. Entries of unmounted surfaces are left behind on purpose; their
   * scopes are unique per mount, so they never match a registered video, and
   * a presented card that remounts stays presented.
   */
  presentedVideoIds: Record<string, string>
  /**
   * The browser refused a muted autoplay (iOS Low Power Mode, a site setting
   * that blocks media). Autoplay stays off for the page; a press still plays.
   */
  autoplayBlocked: boolean
  // Viewer preferences shared by every player, like `globalMuted`: session
  // scoped (not persisted) and never written to `videos`, so changing them does
  // not re-sync the visibility observer. Each player applies them to its own
  // element (`useVideoPlaybackRate`, `useVideoCaptions`).
  playbackRate: number
  captionsEnabled: boolean

  registerVideo: (videoId: string, context: VideoContext) => void
  unregisterVideo: (videoId: string) => void
  setVideoElement: (videoId: string, el: HTMLVideoElement | null) => void
  setContainerElement: (videoId: string, el: HTMLElement | null) => void
  setHlsInstance: (videoId: string, hls: Hls | null) => void
  updateVisibility: (videoId: string, visibility: number) => void
  updateVisibilities: (updates: Iterable<readonly [string, number]>) => void
  /**
   * Resolves which video plays: stops the active video once it has fully left
   * the viewport, then autoplays the slot owner or the most visible candidate.
   * Called on every visibility change and whenever the candidates change.
   */
  recalculateMostVisible: () => void
  /**
   * Pauses every other video and plays `videoId`; `null` pauses and releases
   * whatever plays. `startedBy` defaults to a viewer press
   * (`useVideoPlayback.togglePlay`); the resolver passes 'autoplay'.
   */
  setActiveVideo: (videoId: string | null, startedBy?: PlaybackStarter) => void
  /**
   * A surface instance (identified by its video-id `scope`) now presents
   * `videoId`, or nothing. That video becomes the surface's autoplay
   * candidate, and the surface's own video that is no longer presented stops,
   * even one the viewer started. Never touches another surface's, a floating
   * player's or a stories viewer's video. An empty scope is a no-op.
   */
  presentVideo: (scope: string, videoId: string | null) => void
  setManuallyPaused: (videoId: string, paused: boolean) => void
  toggleMute: () => void
  setViewerActiveStory: (videoId: string | null) => void
  setPinnedActiveVideo: (videoId: string | null) => void
  /** Ignores non-finite or non-positive rates. Does not touch any element. */
  setPlaybackRate: (rate: number) => void
  setCaptionsEnabled: (enabled: boolean) => void
}

function prefersReducedMotion(): boolean {
  // Optional chains throughout: hosts and test doubles stub matchMedia with
  // functions that return nothing, and this also runs from a microtask
  // (`queueResolve`), where a throw would surface as an unhandled error.
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
}

/**
 * Plays `el` for `videoId`. Browsers refuse a programmatic play with sound
 * until the viewer has interacted with the page (iOS Safari: with that very
 * element), so a refused play with sound retries muted, and the shared mute
 * state follows so every control shows Unmute. A refused muted play (iOS Low
 * Power Mode, a blocking site setting) releases the video so its control shows
 * Play; when it was an autoplay, autoplay stays off for the page. Other
 * rejections are expected and ignored: a pause interrupting play() (an
 * AbortError while the viewer scrolls) or an unsupported source, which the
 * player's own error fallback handles.
 */
function playVideo(videoId: string, el: HTMLVideoElement): void {
  el.play()?.catch((error: unknown) => {
    if ((error as { name?: unknown } | null)?.name !== 'NotAllowedError') return
    const state = useVideoPlaybackStore.getState()
    if (state.activeVideoId !== videoId || state.videos[videoId]?.videoElement !== el) return
    if (!el.muted) {
      useVideoPlaybackStore.setState({ globalMuted: true })
      el.muted = true
      playVideo(videoId, el)
      return
    }
    useVideoPlaybackStore.setState({
      activeVideoId: null,
      activeStartedBy: null,
      autoplayBlocked: state.autoplayBlocked || state.activeStartedBy === 'autoplay',
    })
  })
}

/**
 * Resumes playback for `videoId` when it is already the active,
 * non-manually-paused video. `setActiveVideo` plays the video immediately,
 * but it can be activated (by a press, or by autoplay as its card scrolls in)
 * before the video's element or HLS instance is ready (HLS attaches via an
 * async dynamic import). Once either attaches later via
 * `setVideoElement`/`setHlsInstance`, neither call by itself retries playback,
 * and nothing else in `useVideoPlayback` re-triggers it because those hooks
 * only react to `activeVideoId`/`isManuallyPaused` changes, not to the
 * element/HLS instance becoming available. Calling this after both setters
 * closes that gap without polling or timers.
 */
function resumeActivePlayback(state: VideoPlaybackState, videoId: string): void {
  if (state.activeVideoId !== videoId) return
  const info = state.videos[videoId]
  if (!info || info.isManuallyPaused) return
  info.hlsInstance?.startLoad(-1)
  if (info.videoElement) {
    info.videoElement.muted = state.globalMuted
    playVideo(videoId, info.videoElement)
  }
}

/** Pauses and stops HLS loading for every registered video except `videoId`. */
function pauseAllExcept(videos: Record<string, VideoInfo>, videoId: string | null): void {
  for (const [id, info] of Object.entries(videos)) {
    if (id !== videoId) {
      info.videoElement?.pause()
      info.hlsInstance?.stopLoad()
    }
  }
}

let resolveQueued = false

/**
 * Re-resolves playback once the current commit has settled. When the playing
 * player unmounts (its embed removed, its card refetched away), a video that
 * is already in view gets no new IntersectionObserver entry, so nothing would
 * pick it up until the viewer scrolls. A microtask, not a direct call, so a
 * whole subtree unmounting at once is resolved once, after every player in it
 * has unregistered, and never starts a video that is about to go too.
 */
function queueResolve(): void {
  if (resolveQueued) return
  resolveQueued = true
  queueMicrotask(() => {
    resolveQueued = false
    useVideoPlaybackStore.getState().recalculateMostVisible()
  })
}

export const useVideoPlaybackStore = create<VideoPlaybackState>((set, get) => ({
  videos: {},
  containerRevision: 0,
  activeVideoId: null,
  activeStartedBy: null,
  mostVisibleVideoId: null,
  globalMuted: true,
  userMutePreference: true,
  viewerActiveStoryId: null,
  pinnedActiveVideoId: null,
  presentedVideoIds: {},
  autoplayBlocked: false,
  playbackRate: 1,
  captionsEnabled: false,

  registerVideo: (videoId, context) => {
    set((state) => ({
      containerRevision: state.containerRevision + (state.videos[videoId]?.containerElement ? 1 : 0),
      videos: {
        ...state.videos,
        [videoId]: {
          videoId,
          videoElement: null,
          containerElement: null,
          hlsInstance: null,
          visibility: 0,
          context,
          isManuallyPaused: false,
        },
      },
    }))
  },

  unregisterVideo: (videoId) => {
    const state = get()
    const info = state.videos[videoId]
    if (info) {
      info.videoElement?.pause()
      info.hlsInstance?.destroy()
    }
    const { [videoId]: _, ...rest } = state.videos
    const wasActive = state.activeVideoId === videoId
    set({
      videos: rest,
      containerRevision: state.containerRevision + (info?.containerElement ? 1 : 0),
      activeVideoId: wasActive ? null : state.activeVideoId,
      activeStartedBy: wasActive ? null : state.activeStartedBy,
      mostVisibleVideoId: state.mostVisibleVideoId === videoId ? null : state.mostVisibleVideoId,
      // A removed video can no longer own a pin or a story viewer session;
      // clear both so the slot never points at a missing video.
      pinnedActiveVideoId: state.pinnedActiveVideoId === videoId ? null : state.pinnedActiveVideoId,
      viewerActiveStoryId: state.viewerActiveStoryId === videoId ? null : state.viewerActiveStoryId,
    })
    if (wasActive) queueResolve()
  },

  setVideoElement: (videoId, el) => {
    set((state) => {
      const info = state.videos[videoId]
      if (!info) return state
      return {
        videos: {
          ...state.videos,
          [videoId]: { ...info, videoElement: el },
        },
      }
    })
    if (el) resumeActivePlayback(get(), videoId)
  },

  setContainerElement: (videoId, el) => {
    set((state) => {
      const info = state.videos[videoId]
      if (!info || info.containerElement === el) return state
      return {
        containerRevision: state.containerRevision + 1,
        videos: {
          ...state.videos,
          [videoId]: { ...info, containerElement: el },
        },
      }
    })
  },

  setHlsInstance: (videoId, hls) => {
    set((state) => {
      const info = state.videos[videoId]
      if (!info) return state
      return {
        videos: {
          ...state.videos,
          [videoId]: { ...info, hlsInstance: hls },
        },
      }
    })
    if (hls) resumeActivePlayback(get(), videoId)
  },

  updateVisibility: (videoId, visibility) => {
    get().updateVisibilities([[videoId, visibility]])
  },

  updateVisibilities: (updates) => {
    set((state) => {
      let videos = state.videos
      for (const [videoId, visibility] of updates) {
        const info = videos[videoId]
        if (!info || info.visibility === visibility) continue
        if (videos === state.videos) videos = { ...videos }
        videos[videoId] = { ...info, visibility }
      }
      return videos === state.videos ? state : { videos }
    })
  },

  recalculateMostVisible: () => {
    const state = get()
    const owner = state.viewerActiveStoryId ?? state.pinnedActiveVideoId
    const presented = new Set(Object.values(state.presentedVideoIds))
    const isCandidate = (video: VideoInfo) =>
      MOST_VISIBLE_ELIGIBLE_CONTEXTS.includes(video.context) || presented.has(video.videoId)

    // Ties keep the earliest registered video (feed order).
    let best: VideoInfo | null = null
    for (const video of Object.values(state.videos)) {
      if (!isCandidate(video) || video.visibility < AUTOPLAY_MIN_VISIBILITY) continue
      if (!best || video.visibility > best.visibility) best = video
    }
    const bestId = best?.videoId ?? null
    if (bestId !== state.mostVisibleVideoId) {
      set({ mostVisibleVideoId: bestId })
    }

    // The active video stops once it has fully left the viewport (HLS loading
    // stops, the manual-pause flag is kept, so a paused video stays paused when
    // it scrolls back). An autoplayed video also stops once it is no longer a
    // candidate (its stories viewer closed). The slot owner is exempt: the
    // open stories overlay or the floating player is always on screen.
    let activeId = state.activeVideoId
    const active = activeId ? state.videos[activeId] : undefined
    if (
      active
      && activeId !== owner
      && (active.visibility <= 0 || (state.activeStartedBy === 'autoplay' && !isCandidate(active)))
    ) {
      get().setActiveVideo(null)
      activeId = null
    }

    if (state.autoplayBlocked || prefersReducedMotion()) return
    // A press wins, over autoplay and over a slot owner, until its video
    // leaves the viewport or the viewer presses another.
    if (activeId && state.activeStartedBy === 'viewer') return

    if (owner) {
      if (activeId !== owner && !state.videos[owner]?.isManuallyPaused) {
        get().setActiveVideo(owner, 'autoplay')
      }
      return
    }

    // A video the viewer paused in place is not replaced by a less visible one.
    if (!best || best.videoId === activeId || best.isManuallyPaused) return
    const current = activeId ? state.videos[activeId] : undefined
    if (current && best.visibility - current.visibility <= AUTOPLAY_HANDOFF_MARGIN) return
    get().setActiveVideo(best.videoId, 'autoplay')
  },

  setActiveVideo: (videoId, startedBy = 'viewer') => {
    const { videos, globalMuted } = get()

    pauseAllExcept(videos, videoId)

    // Set before play() so a refused play sees this video as the active one.
    set({ activeVideoId: videoId, activeStartedBy: videoId ? startedBy : null })

    if (videoId) {
      const active = videos[videoId]
      if (active) {
        active.hlsInstance?.startLoad(-1)
        if (active.videoElement) {
          active.videoElement.muted = globalMuted
          playVideo(videoId, active.videoElement)
        }
      }
    }
  },

  presentVideo: (scope, videoId) => {
    if (!scope) return
    const { presentedVideoIds } = get()
    if ((presentedVideoIds[scope] ?? null) !== videoId) {
      const { [scope]: _, ...rest } = presentedVideoIds
      set({ presentedVideoIds: videoId ? { ...rest, [scope]: videoId } : rest })
    }
    const { activeVideoId } = get()
    if (activeVideoId !== null && activeVideoId !== videoId && videoIdInScope(scope, activeVideoId)) {
      get().setActiveVideo(null)
    }
    get().recalculateMostVisible()
  },

  setManuallyPaused: (videoId, paused) => {
    set((state) => {
      const info = state.videos[videoId]
      if (!info) return state
      return {
        videos: {
          ...state.videos,
          [videoId]: { ...info, isManuallyPaused: paused },
        },
      }
    })
  },

  toggleMute: () => {
    const state = get()
    const newMuted = !state.globalMuted
    set({ globalMuted: newMuted, userMutePreference: newMuted })

    if (state.activeVideoId) {
      const active = state.videos[state.activeVideoId]
      if (active?.videoElement) {
        active.videoElement.muted = newMuted
      }
    }
  },

  setViewerActiveStory: (videoId) => {
    const state = get()
    set({ viewerActiveStoryId: videoId })
    if (videoId) {
      // The story owns the slot: everything else stops, and the resolver
      // autoplays the story.
      pauseAllExcept(state.videos, videoId)
      if (state.activeVideoId !== videoId) set({ activeVideoId: null, activeStartedBy: null })
    }
    get().recalculateMostVisible()
  },

  setPinnedActiveVideo: (videoId) => {
    const state = get()
    set({ pinnedActiveVideoId: videoId })
    if (videoId) {
      // Same as a story viewer: pause the others, autoplay the headline.
      pauseAllExcept(state.videos, videoId)
      if (state.activeVideoId !== videoId) set({ activeVideoId: null, activeStartedBy: null })
    }
    get().recalculateMostVisible()
  },

  setPlaybackRate: (rate) => {
    if (!Number.isFinite(rate) || rate <= 0) return
    if (get().playbackRate === rate) return
    set({ playbackRate: rate })
  },

  setCaptionsEnabled: (enabled) => {
    if (get().captionsEnabled === enabled) return
    set({ captionsEnabled: enabled })
  },
}))
